import { ArithmeticParams, GeometricParams, SequenceSeriesSliderConfig } from './sequence-series.model';

/**
 * Structured presets for the SequenceSeriesComponent.
 *
 * Each preset maps to the component's inputs: { params, sliderConfig?, height? }.
 * These are the **single source of truth** — both Storybook stories and the
 * study-notes editor import from here.
 *
 * In a custom_component block, use a preset as a base and spread overrides:
 *
 * ```json
 * {
 *   "component_id": "sequence-series-chart",
 *   "preset": "geometric-explainer",
 *   "inputs": { "params": { "commonFactor": 0.5 } }
 * }
 * ```
 */

// ── Shared slider configs ──────────────────────────────────

export const DEFAULT_SLIDER: SequenceSeriesSliderConfig = {
  firstTerm: { min: -20, max: 20, step: 0.5 },
  commonFactor: { min: -10, max: 10, step: 0.5 },
};

export const RATIO_SLIDER: SequenceSeriesSliderConfig = {
  firstTerm: { min: -20, max: 20, step: 0.5 },
  commonFactor: { min: -3, max: 3, step: 0.1 },
};

export const CONVERGING_RATIO_SLIDER: SequenceSeriesSliderConfig = {
  firstTerm: { min: -20, max: 20, step: 0.5 },
  commonFactor: { min: -0.99, max: 0.99, step: 0.01 },
};

// ── Arithmetic presets ─────────────────────────────────────

export const ARITHMETIC_BASE = {
  params: {
    mode: 'arithmetic',
    firstTerm: 2,
    commonFactor: 3,
    showSeries: false,
    range: { from: 1, to: 10 },
  } satisfies ArithmeticParams,
};

export const ARITHMETIC_EXPLAINER = {
  params: {
    ...ARITHMETIC_BASE.params,
    firstTerm: 10,
    showSliders: true,
    explainerMode: true,
    range: { from: 1, to: 8 },
  } satisfies ArithmeticParams,
  sliderConfig: DEFAULT_SLIDER,
};

export const ARITHMETIC_SERIES = {
  params: {
    ...ARITHMETIC_BASE.params,
    showSeries: true,
    showSliders: true,
    showValues: true,
  } satisfies ArithmeticParams,
  sliderConfig: DEFAULT_SLIDER,
};

export const ARITHMETIC_DECREASING = {
  params: {
    ...ARITHMETIC_BASE.params,
    firstTerm: 20,
    commonFactor: -3,
    showSeries: true,
    range: { from: 1, to: 7 },
  } satisfies ArithmeticParams,
};

export const ARITHMETIC_NEGATIVE_START = {
  params: {
    ...ARITHMETIC_BASE.params,
    firstTerm: -8,
    showSeries: true,
    range: { from: 1, to: 8 },
  } satisfies ArithmeticParams,
};

// ── Geometric presets ──────────────────────────────────────

export const GEOMETRIC_BASE = {
  params: {
    mode: 'geometric',
    firstTerm: 3,
    commonFactor: 2,
    showSeries: false,
    range: { from: 1, to: 8 },
  } satisfies GeometricParams,
};

export const GEOMETRIC_EXPLAINER = {
  params: {
    ...GEOMETRIC_BASE.params,
    showSliders: true,
    explainerMode: true,
  } satisfies GeometricParams,
  sliderConfig: RATIO_SLIDER,
};

export const GEOMETRIC_SERIES = {
  params: {
    ...GEOMETRIC_BASE.params,
    showSeries: true,
    showValues: true,
  } satisfies GeometricParams,
};

export const GEOMETRIC_DECAYING = {
  params: {
    ...GEOMETRIC_BASE.params,
    firstTerm: 100,
    commonFactor: 0.5,
    showSeries: true,
    showSumToInfinity: true,
    range: { from: 1, to: 10 },
  } satisfies GeometricParams,
};

export const GEOMETRIC_CONVERGING = {
  params: {
    ...GEOMETRIC_BASE.params,
    firstTerm: 10,
    commonFactor: -0.5,
    showSeries: true,
    showSumToInfinity: true,
    showSliders: true,
    range: { from: 1, to: 15 },
  } satisfies GeometricParams,
  sliderConfig: CONVERGING_RATIO_SLIDER,
};

// ── Behaviour comparison (used in 3-column table) ─────────

export const GEOMETRIC_BEHAVIOUR_INCREASING = {
  params: {
    ...GEOMETRIC_BASE.params,
    firstTerm: 4,
    commonFactor: 3,
    range: { from: 1, to: 5 },
  } satisfies GeometricParams,
};

export const GEOMETRIC_BEHAVIOUR_DECREASING = {
  params: {
    ...GEOMETRIC_BASE.params,
    firstTerm: 25,
    commonFactor: 0.2,
    range: { from: 1, to: 5 },
  } satisfies GeometricParams,
};

export const GEOMETRIC_BEHAVIOUR_ALTERNATING = {
  params: {
    ...GEOMETRIC_BASE.params,
    firstTerm: 4,
    commonFactor: -3,
    range: { from: 1, to: 5 },
  } satisfies GeometricParams,
};

// ── Interactive (with sliders) ─────────────────────────────

export const ARITHMETIC_WITH_SLIDERS = {
  params: {
    ...ARITHMETIC_BASE.params,
    showSeries: true,
    showSliders: true,
  } satisfies ArithmeticParams,
  sliderConfig: DEFAULT_SLIDER,
};

export const GEOMETRIC_WITH_SLIDERS = {
  params: {
    ...GEOMETRIC_BASE.params,
    showSeries: true,
    showSliders: true,
    showValues: true,
  } satisfies GeometricParams,
  sliderConfig: RATIO_SLIDER,
};

// ── Large range ────────────────────────────────────────────

export const LARGE_SEQUENCE = {
  params: {
    ...ARITHMETIC_BASE.params,
    firstTerm: 1,
    commonFactor: 1,
    showSeries: true,
    range: { from: 1, to: 20 },
  } satisfies ArithmeticParams,
};

export const DATA_ZOOM = {
  params: {
    ...ARITHMETIC_BASE.params,
    firstTerm: 1,
    commonFactor: 2,
    showSeries: true,
    range: { from: 1, to: 100 },
  } satisfies ArithmeticParams,
};

// ── Preset registry (keyed by name) ─────────────────────────

export const SEQUENCE_SERIES_PRESETS: Record<string, Record<string, unknown>> = {
  'arithmetic-base': ARITHMETIC_BASE,
  'arithmetic-explainer': ARITHMETIC_EXPLAINER,
  'arithmetic-series': ARITHMETIC_SERIES,
  'arithmetic-decreasing': ARITHMETIC_DECREASING,
  'arithmetic-negative-start': ARITHMETIC_NEGATIVE_START,
  'geometric-base': GEOMETRIC_BASE,
  'geometric-explainer': GEOMETRIC_EXPLAINER,
  'geometric-series': GEOMETRIC_SERIES,
  'geometric-decaying': GEOMETRIC_DECAYING,
  'geometric-converging': GEOMETRIC_CONVERGING,
  'geometric-behaviour-increasing': GEOMETRIC_BEHAVIOUR_INCREASING,
  'geometric-behaviour-decreasing': GEOMETRIC_BEHAVIOUR_DECREASING,
  'geometric-behaviour-alternating': GEOMETRIC_BEHAVIOUR_ALTERNATING,
  'arithmetic-with-sliders': ARITHMETIC_WITH_SLIDERS,
  'geometric-with-sliders': GEOMETRIC_WITH_SLIDERS,
  'large-sequence': LARGE_SEQUENCE,
  'data-zoom': DATA_ZOOM,
};
